import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["filterForm", "validationMessage", "clientHint", "filtersWrapper", "groupingWrapper",
                     "headlineRow", "childHeadlineRow", "detailRow", "chevron", "expandButton",
                     "pivotWrapper", "measurePicker", "measureChips", "measureCheckbox", "measureLimitMessage",
                     "measureLimitLabel", "measureLimitMessageCount", "chartMeasureDropdown"]

  connect() {
    // Track which group keys are expanded so the UI state survives DOM mutations
    this.expanded = new Set()
    this.#applyMeasureLimitState()
  }

  // Update the measure cap when the pivot axis toggles. Both pivots share the
  // same 10-option chip set; only the cap differs (cycle = 10, group = 3).
  // The server-side query also enforces this cap via MAX_MEASURES.
  pivotChanged(event) {
    const value = event.target.value
    const max = value === "cycle" ? 10 : 3
    if (this.hasMeasurePickerTarget) {
      this.measurePickerTarget.dataset.maxMeasures = max
    }
    if (this.hasMeasureLimitLabelTarget) {
      this.measureLimitLabelTarget.textContent = max
    }
    if (this.hasMeasureLimitMessageCountTarget) {
      this.measureLimitMessageCountTarget.textContent = max
    }
    this.#applyMeasureLimitState()
  }

  // Enforce the per-pivot measure cap. When at the cap, disable the remaining
  // unchecked checkboxes; otherwise re-enable everything.
  measureToggled(_event) {
    this.#applyMeasureLimitState()
  }

  // Toggle between Table and Chart display modes. Hides the table measure
  // picker in chart mode and the chart measure dropdown in table mode. Also
  // disables the pivot toggle when in chart mode (pivot is a no-op for charts).
  displayModeChanged(event) {
    const chartMode = event.target.value === "chart"

    if (this.hasMeasurePickerTarget) {
      this.measurePickerTarget.classList.toggle("hidden", chartMode)
    }
    if (this.hasChartMeasureDropdownTarget) {
      this.chartMeasureDropdownTarget.classList.toggle("hidden", !chartMode)
    }

    // Disable/enable the pivot radios with a tooltip when chart mode is on.
    if (this.hasPivotWrapperTarget) {
      this.pivotWrapperTarget.querySelectorAll('input[name="pivot_axis"]').forEach(input => {
        input.disabled = chartMode
        const label = input.closest("label")
        if (label) {
          label.classList.toggle("opacity-40", chartMode)
          label.classList.toggle("cursor-not-allowed", chartMode)
          if (chartMode) {
            label.setAttribute("title", "Pivot affects table only")
          } else {
            label.removeAttribute("title")
          }
        }
      })
    }
  }

  #currentMaxMeasures() {
    if (this.hasMeasurePickerTarget) {
      const parsed = parseInt(this.measurePickerTarget.dataset.maxMeasures, 10)
      if (!Number.isNaN(parsed) && parsed > 0) return parsed
    }
    return 3
  }

  #applyMeasureLimitState() {
    if (!this.hasMeasureCheckboxTarget) return
    const max = this.#currentMaxMeasures()
    const checked = this.measureCheckboxTargets.filter(cb => cb.checked)
    const atLimit = checked.length >= max

    this.measureCheckboxTargets.forEach(cb => {
      if (!cb.checked) {
        cb.disabled = atLimit
        cb.closest("label")?.classList.toggle("opacity-40", atLimit)
        cb.closest("label")?.classList.toggle("cursor-not-allowed", atLimit)
      } else {
        cb.disabled = false
        cb.closest("label")?.classList.remove("opacity-40", "cursor-not-allowed")
      }
    })

    if (this.hasMeasureLimitMessageTarget) {
      this.measureLimitMessageTarget.classList.toggle("hidden", !atLimit)
    }
  }

  filterChanged(event) {
    const changedFilter = event.target.dataset.filterName
    const form = this.filterFormTarget

    if (changedFilter === "client") this.hideValidationError()

    this.clearDependentFilters(changedFilter)

    const formData = new FormData(form)
    const params = new URLSearchParams(formData)
    params.set("changed_filter", changedFilter)

    const url = `/distribution_trend/filter_options?${params.toString()}`

    fetch(url, {
      headers: {
        "Accept": "text/vnd.turbo-stream.html"
      }
    })
    .then(response => response.text())
    .then(html => {
      Turbo.renderStreamMessage(html)

      if (changedFilter === "client") {
        const hasClient = event.target.value !== ""
        if (this.hasClientHintTarget) this.clientHintTarget.classList.toggle("hidden", hasClient)
        if (this.hasFiltersWrapperTarget) {
          this.filtersWrapperTarget.classList.toggle("opacity-40", !hasClient)
          this.filtersWrapperTarget.classList.toggle("pointer-events-none", !hasClient)
          this.filtersWrapperTarget.classList.toggle("select-none", !hasClient)
        }
        if (this.hasGroupingWrapperTarget) {
          this.groupingWrapperTarget.classList.toggle("opacity-40", !hasClient)
          this.groupingWrapperTarget.classList.toggle("pointer-events-none", !hasClient)
          this.groupingWrapperTarget.classList.toggle("select-none", !hasClient)
        }
      }
    })
  }

  // When a grouping level changes, clear any downstream selections so we don't
  // leave a duplicate selection in place. Server-rendered exclusions are
  // applied on the next form submit (Generate Report). We deliberately do not
  // submit the form here -- the report only regenerates when the user clicks
  // Generate Report.
  groupingChanged(event) {
    const level = parseInt(event.target.dataset.groupingLevel, 10)

    if (level === 1) {
      this.#setSelectValue("group_2", "")
      this.#setSelectValue("group_3", "")
    } else if (level === 2) {
      this.#setSelectValue("group_3", "")
    }
  }

  generateReport(event) {
    event.preventDefault()
    const clientSelect = this.filterFormTarget.querySelector('[name="client_id"]')
    if (!clientSelect || !clientSelect.value) {
      this.showValidationError("Please select a client before generating the report.")
      return
    }

    this.hideValidationError()
    this.filterFormTarget.requestSubmit()
  }

  expandRow(event) {
    event.preventDefault()
    const button = event.currentTarget
    const groupKey = button.dataset.distributionTrendGroupKeyParam
    if (!groupKey) return

    const isExpanded = this.expanded.has(groupKey)
    if (isExpanded) {
      this.expanded.delete(groupKey)
    } else {
      this.expanded.add(groupKey)
    }

    // Toggle detail rows belonging to this group
    this.detailRowTargets
      .filter(row => row.dataset.distributionTrendGroupKey === groupKey)
      .forEach(row => row.classList.toggle("hidden", isExpanded))

    // Toggle immediate child headline rows (one level down)
    this.childHeadlineRowTargets
      .filter(row => row.dataset.distributionTrendParentKey === groupKey)
      .forEach(row => {
        row.classList.toggle("hidden", isExpanded)
        // When collapsing, also collapse any descendants
        if (isExpanded) this.#collapseDescendants(row.dataset.distributionTrendGroupKey)
      })

    // Rotate chevron glyph
    const chevron = this.chevronTargets.find(c => c.dataset.distributionTrendGroupKey === groupKey)
    if (chevron) chevron.textContent = isExpanded ? "▸" : "▾"
  }

  showValidationError(message) {
    if (!this.hasValidationMessageTarget) return
    this.validationMessageTarget.textContent = message
    this.validationMessageTarget.classList.remove("hidden")
  }

  hideValidationError() {
    if (!this.hasValidationMessageTarget) return
    this.validationMessageTarget.classList.add("hidden")
  }

  clearDependentFilters(changedFilter) {
    const hierarchy = {
      "client":   ["sector_id", "retailer_kind_id", "brand_id", "ph1_id", "ph2_id", "ph3_id", "retailer_id", "banner_id", "place_owner_id[]"],
      "sector":   ["retailer_kind_id"],
      "ph1":      ["ph2_id", "ph3_id"],
      "ph2":      ["ph3_id"],
      "retailer": ["banner_id", "place_owner_id[]"]
    }

    const toClear = hierarchy[changedFilter] || []
    toClear.forEach(fieldName => {
      const field = this.filterFormTarget.querySelector(`[name="${fieldName}"]`)
      if (field) {
        if (field.multiple) {
          Array.from(field.options).forEach(opt => opt.selected = false)
          field.dispatchEvent(new Event("change"))
        } else {
          field.value = ""
        }
      }
    })
  }

  #setSelectValue(name, value) {
    const select = this.filterFormTarget.querySelector(`[name="${name}"]`)
    if (select) select.value = value
  }

  #collapseDescendants(parentKey) {
    this.expanded.delete(parentKey)

    this.detailRowTargets
      .filter(row => row.dataset.distributionTrendGroupKey === parentKey)
      .forEach(row => row.classList.add("hidden"))

    const childChevron = this.chevronTargets.find(c => c.dataset.distributionTrendGroupKey === parentKey)
    if (childChevron) childChevron.textContent = "▸"

    this.childHeadlineRowTargets
      .filter(row => row.dataset.distributionTrendParentKey === parentKey)
      .forEach(row => {
        row.classList.add("hidden")
        this.#collapseDescendants(row.dataset.distributionTrendGroupKey)
      })
  }
}
